import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import { toast } from "react-toastify";
import * as companyService from "../../services/companyCustomerService";

// Same limits the API enforces (companyCustomer.service.js); the inputs stop typing at them.
const FIELDS = [
  { key: "name", label: "Company Name", max: 200 },
  { key: "trn", label: "TRN (Tax Registration Number)", max: 50 },
  { key: "phone_number", label: "Phone Number", max: 30 },
  { key: "email", label: "Email", max: 150 },
  { key: "address", label: "Address", max: 500 },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function initials(name) {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] || "") + (words[1]?.[0] || "")).toUpperCase() || "?";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" });
}

const pageStyle = { background: "#f8fafc", minHeight: "100vh", padding: "24px 28px" };
const cardStyle = { background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)" };

const formOf = (company) => ({
  name: company?.name || "",
  trn: company?.trn || "",
  phone_number: company?.phone_number || "",
  email: company?.email || "",
  address: company?.address || "",
});

// A company (B2B bill-to customer): its details and the same "Edit Information" flow an
// individual customer has. Editing changes invoices issued from now on - an issued invoice keeps
// the details it was issued with.
export default function CompanyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Paint instantly from the row the user clicked, then refresh with the real record.
  const [company, setCompany] = useState(location.state?.companyData || null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const backToList = () => navigate("/customers?tab=companies");

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await companyService.getCompany(id);
      if (res?.success && res.data) {
        setCompany(res.data);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.error("fetchDetail error:", err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // ── Edit company information ─────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  // What openEdit loaded, so saveEdit can tell "untouched" from "actively cleared": a field that
  // was already blank doesn't error just because the form was opened and saved.
  const [originalForm, setOriginalForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const openEdit = () => {
    const initial = formOf(company);
    setEditForm(initial);
    setOriginalForm(initial);
    setEditError("");
    setFieldErrors({});
    setEditing(true);
  };

  const saveEdit = async () => {
    setEditError("");
    setFieldErrors({});

    const touched = (key) => editForm[key] !== originalForm[key];
    if (!FIELDS.some((f) => touched(f.key))) {
      setEditError("No changes detected");
      return;
    }

    // Validation only applies to fields the user actually edited.
    const errors = {};
    if (touched("name") && !editForm.name.trim()) errors.name = "Company name is required";
    if (touched("email") && editForm.email.trim() && !EMAIL_RE.test(editForm.email.trim())) {
      errors.email = "Enter a valid email address";
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    try {
      setSaving(true);
      const payload = Object.fromEntries(FIELDS.map((f) => [f.key, editForm[f.key].trim()]));
      const res = await companyService.updateCompany(id, payload);
      if (res?.success) {
        setCompany(res.data || { ...company, ...payload });
        setEditing(false);
        toast.success("Company updated");
      } else if (res?.field) {
        // The server's own rule (a duplicate name, a too-long value ...), under its input.
        setFieldErrors({ [res.field]: res.message });
      } else {
        setEditError(res?.message || "Could not save the changes");
      }
    } catch (err) {
      console.error("saveEdit error:", err);
      setEditError(err?.message || "Could not save the changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !company) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, padding: 56, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>Loading company…</div>
      </div>
    );
  }

  if (notFound && !company) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, padding: 56, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
          Company not found.
          <div style={{ marginTop: 14 }}>
            <button onClick={backToList} style={{ padding: "8px 18px", border: "none", borderRadius: 9, background: "#d01c4c", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              ← Back to Companies
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <button
        onClick={backToList}
        style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "none", color: "#d01c4c", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 18 }}
      >
        <MdArrowBack size={16} /> Back to Companies
      </button>

      {/* Profile header */}
      <div style={{ ...cardStyle, padding: 22, marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: "#fef3c7", color: "#b45309", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700 }}>
            {initials(company?.name)}
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 19, fontWeight: 800, color: "#0f172a" }}>{company?.name || "—"}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#b45309", background: "#fef3c7", padding: "2px 9px", borderRadius: 20 }}>Company</span>
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              {company?.phone_number || "—"} &nbsp;&nbsp; {company?.email || ""}
            </div>
          </div>
        </div>
        <button
          onClick={openEdit}
          style={{ padding: "8px 16px", border: "1.5px solid #e2e8f0", borderRadius: 9, background: "#fff", fontSize: 12.5, fontWeight: 700, color: "#334155", cursor: "pointer" }}
        >
          ✎ Edit Information
        </button>
      </div>

      {/* Company details */}
      <div style={{ ...cardStyle, padding: 20, maxWidth: 640 }}>
        <h4 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Company Information</h4>
        {[
          ["Company Since", formatDate(company?.created_at)],
          ["TRN", company?.trn || "—"],
          ["Phone", company?.phone_number || "—"],
          ["Email", company?.email || "—"],
          ["Address", company?.address || "—"],
        ].map(([label, value]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 24, padding: "8px 0", borderBottom: "1px solid #f8fafc", fontSize: 13 }}>
            <span style={{ color: "#94a3b8", flexShrink: 0 }}>{label}</span>
            <span style={{ color: "#0f172a", fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
          </div>
        ))}
      </div>

      {/* Edit company modal */}
      {editing && editForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div role="dialog" aria-label="Edit Company Information" style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 440, maxHeight: "92vh", overflowY: "auto" }}>
            <h4 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Edit Company Information</h4>
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#94a3b8" }}>
              Changes apply to invoices issued from now on. Invoices already issued keep the details they were issued with.
            </p>
            {FIELDS.map(({ key, label, max }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label htmlFor={`company-${key}`} style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>
                  {label}
                </label>
                <input
                  id={`company-${key}`}
                  type="text"
                  maxLength={max}
                  value={editForm[key]}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditForm((f) => ({ ...f, [key]: val }));
                    if (fieldErrors[key]) setFieldErrors((fe) => ({ ...fe, [key]: "" }));
                  }}
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: 8,
                    border: `1.5px solid ${fieldErrors[key] ? "#dc2626" : "#e2e8f0"}`,
                    fontSize: 13,
                    boxSizing: "border-box",
                    outline: "none",
                    background: "#fff",
                    color: "#0f172a",
                  }}
                />
                {fieldErrors[key] && <div style={{ color: "#dc2626", fontSize: 11.5, marginTop: 4 }}>{fieldErrors[key]}</div>}
              </div>
            ))}
            {editError && <div role="alert" style={{ color: "#dc2626", fontSize: 12.5, marginBottom: 12 }}>{editError}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={saveEdit} disabled={saving} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#d01c4c", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {saving ? "Saving…" : "Save Changes"}
              </button>
              <button onClick={() => setEditing(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#fff", color: "#334155", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
